import { Routes } from '@angular/router';
import { AuthGuard } from './core/guards/auth.guard';
import { AuditorSelfProfileAccessGuard } from './core/guards/auditor-self-profile-access.guard';
import { AuditorsMarketplaceAccessGuard } from './core/guards/auditors-marketplace-access.guard';
import { NoAuthGuard } from './core/guards/no-auth.guard';
import { OrderServiceTypeGuard } from './core/guards/order-service-type.guard';
import { RoleSelectionGuard } from './core/guards/role-selection.guard';
import { orderAddOnsResolver$ } from './pages/product-sites-facilities/shared/resolvers/order-add-ons.resolver$';

export const routes: Routes = [
  { path: '', redirectTo: '/login', pathMatch: 'full' },
  {
    path: 'login',
    loadComponent: () => import('./pages/login/login.component').then(m => m.LoginComponent),
    canActivate: [NoAuthGuard],
  },
  {
    path: 'callback',
    loadComponent: () => import('./pages/callback/callback.component').then(m => m.CallbackComponent),
  },
  {
    path: 'error',
    loadComponent: () => import('./pages/error/error-page.component').then(m => m.ErrorPageComponent),
  },
  {
    path: 'login/role',
    loadComponent: () => import('./pages/role-selection/role-selection.component').then(m => m.RoleSelectionComponent),
    canActivate: [AuthGuard],
  },
  {
    path: 'home',
    loadComponent: () => import('./pages/home/home.component').then(m => m.HomeComponent),
    canActivate: [AuthGuard, RoleSelectionGuard],
  },
  {
    path: 'orders/to-be-scheduled',
    loadComponent: () => import('./pages/scheduler/scheduler.component').then(m => m.SchedulerComponent),
    canActivate: [AuthGuard, RoleSelectionGuard],
  },
  {
    path: 'calendar',
    loadComponent: () => import('./pages/calendar/calendar.component').then(m => m.CalendarComponent),
    canActivate: [AuthGuard, RoleSelectionGuard],
  },
  {
    path: 'auditors',
    loadComponent: () => import('./pages/auditors/auditors.component').then(m => m.AuditorsComponent),
    canActivate: [AuthGuard, RoleSelectionGuard, AuditorsMarketplaceAccessGuard],
    data: { pageTitleKey: 'AUDITORS.MARKETPLACE_TITLE' },
  },
  {
    path: 'auditor/:auditorId',
    loadComponent: () => import('./pages/assessor-detail/assessor-detail.component').then(m => m.AssessorDetailComponent),
    canActivate: [AuthGuard, RoleSelectionGuard, AuditorSelfProfileAccessGuard],
    canActivateChild: [AuthGuard, RoleSelectionGuard, AuditorSelfProfileAccessGuard],
    data: { pageTitleKey: 'AUDITORS.MARKETPLACE_TITLE' },
    children: [
      { path: '', redirectTo: 'overview', pathMatch: 'full' },
      {
        path: 'overview',
        loadComponent: () =>
          import('./pages/assessor-detail/pages/assessor-overview/assessor-overview.component').then(m => m.AssessorOverviewComponent),
      },
      {
        path: 'schemes',
        loadComponent: () =>
          import('./pages/assessor-detail/pages/assessor-schemes/assessor-schemes.component').then(m => m.AssessorSchemesComponent),
      },
      {
        path: 'availability',
        loadComponent: () =>
          import('./pages/assessor-detail/pages/assessor-availability/assessor-availability.component').then(
            m => m.AssessorAvailabilityComponent
          ),
      },
      {
        path: 'coi-clients',
        loadComponent: () =>
          import('./pages/assessor-detail/pages/assessor-coi-clients/assessor-coi-clients.component').then(
            m => m.AssessorCoiClientsComponent
          ),
      },
      {
        path: 'reviews',
        loadComponent: () =>
          import('./pages/assessor-detail/pages/assessor-reviews/assessor-reviews.component').then(m => m.AssessorReviewsComponent),
      },
      {
        path: 'documents',
        loadComponent: () =>
          import('./pages/assessor-detail/pages/assessor-documents/assessor-documents.component').then(m => m.AssessorDocumentsComponent),
        data: { titleKey: 'ASSESSOR_DETAIL.TABS.DOCUMENTS' },
      },
      {
        path: 'history',
        loadComponent: () =>
          import('./pages/assessor-detail/pages/assessor-history/assessor-history.component').then(m => m.AssessorHistoryComponent),
        data: { titleKey: 'ASSESSOR_DETAIL.TABS.HISTORY' },
      },
    ],
  },
  {
    path: 'order/:orderNumber',
    loadComponent: () => import('./pages/order-detail/order-detail.component').then(m => m.OrderDetailComponent),
    canActivate: [AuthGuard, RoleSelectionGuard, OrderServiceTypeGuard],
    canActivateChild: [AuthGuard, RoleSelectionGuard, OrderServiceTypeGuard],
    children: [
      { path: '', redirectTo: 'audit-details', pathMatch: 'full' },
      {
        path: 'audit-details',
        loadComponent: () => import('./pages/audit-details/audit-details.component').then(m => m.AuditDetailsComponent),
      },
      {
        path: 'company-info',
        loadComponent: () => import('./pages/company-info/company-info.component').then(m => m.CompanyInfoComponent),
      },
      {
        path: 'general-info',
        loadComponent: () => import('./pages/general-info/general-info.component').then(m => m.GeneralInfoComponent),
      },
      {
        path: 'preferences',
        loadComponent: () => import('./pages/preferences/preferences.component').then(m => m.PreferencesComponent),
      },
      {
        path: 'product-sites-facilities',
        loadComponent: () =>
          import('./pages/product-sites-facilities/product-sites-facilities.component').then(m => m.ProductSitesFacilitiesComponent),
        resolve: {
          orderAddOns: orderAddOnsResolver$,
        },
      },
      {
        path: 'order-validation',
        loadComponent: () => import('./pages/order-validation/order-validation.component').then(m => m.OrderValidationComponent),
      },
      {
        path: 'scheduling',
        loadComponent: () => import('./pages/scheduling/scheduling.component').then(m => m.SchedulingComponent),
      },
      {
        path: 'audit',
        loadComponent: () => import('./pages/audit/audit.component').then(m => m.AuditComponent),
      },
      {
        path: 'corrective-actions',
        loadComponent: () => import('./pages/corrective-actions/corrective-actions.component').then(m => m.CorrectiveActionsComponent),
      },
      {
        path: 'certificate-review',
        loadComponent: () => import('./pages/certificate-review/certificate-review.component').then(m => m.CertificateReviewComponent),
      },
    ],
  },
  // Wildcard route to redirect not found pages to login
  { path: '**', redirectTo: '/login' },
];
